import React, { useCallback, useEffect } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { Modal, Button, Form, Col, Row } from "react-bootstrap";
import { Data } from "../../../../components/Formatters/DateTime";
import useApiClient, { apiRoutes } from "../../../../hooks/apiClient";
import FormAlert from "../../../../components/UI/FormAlert/FormAlert";
import TextAreaInputFormik from "../../../../components/UI/TextInput/TextAreaInputFormik";
import TextInputNumberFormik from "../../../../components/UI/TextInput/TextInputNumberFormik";

const validationSchema = Yup.object().shape({
  sqAcaoFiscal: Yup.string().required("Sequencial da Ação Fiscal obrigatório."),
  sqPessoa: Yup.string().required("Sequencial de Pessoa obrigatório."),
  dtInicio: Yup.string().required("Data Inicial obrigatória."),
  dtFim: Yup.string().required("Data Final obrigatória."),
  txMotivacao: Yup.string().required("Texto da Motivação é obrigatório."),
  txFundamentacao: Yup.string().required("Texto da Fundamentação Legal obrigatório."),
  txTermoLavratura: Yup.string().required("O Termo de Lavratura obrigatório."),
  vlImposto: Yup.mixed().required("Valor do Imposto é obrigatório."),
  vlMulta: Yup.mixed().required("Valor da Multa é obrigatório."),
});

const initialValues = {};

const EfisModalInserirAssociacao = ({
  title,
  body,
  show,
  sqAF,
  arquivosSelecionados,
  secondaryLabel,
  closeHandler,
  onUpdate,
}) => {
  const row = arquivosSelecionados[0];
  const { request, data, errors, requestId, isLoading } = useApiClient();

  const handleSubmit = useCallback(
    (values) => {
      if (row?.sqPAF) {
        request(apiRoutes.fiscalizacao.ordemservico.alterartsps, values);
      } else {
        request(apiRoutes.fiscalizacao.ordemservico.inserirtsps, values);
      }
    },
    [request, row?.sqPAF],
  );

  if (row?.sqPAF) {
    validationSchema._nodes.forEach((key) => {
      if (key === "sqAcaoFiscal") {
        initialValues[key] = row?.sqAcaoFiscal;
      } else if (key === "sqPessoa") {
        initialValues[key] = row?.sqPessoa;
      } else if (key === "dtInicio") {
        initialValues[key] = row?.dtInicio;
      } else if (key === "dtFim") {
        initialValues[key] = row?.dtFinal;
      } else if (key === "txMotivacao") {
        initialValues[key] = row?.txMotivacao;
      } else if (key === "txFundamentacao") {
        initialValues[key] = row?.txFundamentacao;
      } else if (key === "txTermoLavratura") {
        initialValues[key] = row?.txTermoLavratura;
      } else if (key === "vlImposto") {
        initialValues[key] = row?.vlImposto;
      } else if (key === "vlMulta") {
        initialValues[key] = row?.vlMulta;
      } else {
        initialValues[key] = "";
      }
    });
  } else {
    validationSchema._nodes.forEach((key) => {
      if (key === "vlImposto" || key === "vlMulta") {
        initialValues[key] = 0;
      } else if (key === "sqAcaoFiscal") {
        initialValues[key] = sqAF;
      } else if (key === "sqPessoa") {
        initialValues[key] = row?.sqPessoa;
      } else if (key === "dtInicio") {
        initialValues[key] = row?.dtInicio;
      } else if (key === "dtFim") {
        initialValues[key] = row?.dtFim;
      } else if (key === "txTermoLavratura") {
        initialValues[key] =
          `No exercício das funções de Auditor(es) Fiscal(is), ante o exposto no Processo acima citado, restou caracterizada ` +
          `a sujeição passiva solidária nos termos dos dispositivos supracitados da Lei nº 5.172/1966 (CTN). Assim sendo, fica o ` +
          `sujeito passivo solidário acima identificado CIENTIFICADO da exigência tributária de que trata o Auto de Infração supramencionado, ` +
          `cuja cópia, juntamente com o presente Termo, é entregue neste ato, o qual, para surtir os efeitos legais, foi lavrado em duas ` +
          `vias de igual teor e forma, assinado pelo(s) Auditor(es) Fiscal(is) e pelo responsável solidário ou seu representante legal, ` +
          `nos termos do art. 40-B do RPAT, aprovado pelo Decreto n.º 13.796/1998.`;
      } else {
        initialValues[key] = "";
      }
    });
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if ((data || errors) && !isLoading && formik.isSubmitting) {
      formik.setSubmitting(false);
      if (errors) {
        toast.error(errors.message);
        return;
      }

      if (data) {
        if (row?.sqPAF) {
          toast.warning("Associação Alterada com sucesso!");
        } else {
          toast.success("Associação Adicionada com sucesso!");
        }

        formik.handleReset();
        onUpdate();
        closeHandler();
      }
    }

    if (requestId === "load" && data && !formik.values.loaded) {
      formik.setValues({ ...data.result, loaded: true });
    }

    if (errors && !isLoading) formik.setErrors({ ...formik.errors, ...errors });
  }, [formik, data, errors, requestId, isLoading, onUpdate, closeHandler, row?.sqPAF]);

  return (
    <Modal show={show} size="lg" onHide={() => closeHandler()} backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title>{title || "Associar Representantes a TSPS"}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        Representante: <strong>{row?.txNmFormal}</strong>
        <br />
        Data Início Vínculo:{" "}
        <strong>{row?.dtInicio === "0001-01-01T00:00:00" ? <strong>-</strong> : <Data value={row?.dtInicio} />}</strong>
        <br />
        Data Fim Vínculo:{" "}
        <strong>{row?.dtFim === "0001-01-01T00:00:00" ? <strong>-</strong> : <Data value={row?.dtFim} />}</strong>
        <br />
        <br />
        {typeof body === "string" ? <p className="alerta">{body}</p> : body}
        <FormAlert formik={formik} data={data} errors={errors} isLoading={isLoading} />
        <Form noValidate onSubmit={formik.handleSubmit}>
          <Form.Label>Motivação</Form.Label>
          <TextAreaInputFormik formik={formik} field="txMotivacao" type="string" rows="3" />
          <Form.Label style={{ marginTop: 10 }}>Fundamentação Legal</Form.Label>
          <TextAreaInputFormik formik={formik} field="txFundamentacao" type="string" rows="3" />
          <Form.Label style={{ marginTop: 10 }}>Termo de Lavratura</Form.Label>
          <TextAreaInputFormik formik={formik} field="txTermoLavratura" type="string" rows="3" />
          <Row>
            <Col sm="4">
              <Form.Label style={{ marginTop: 10 }}>Valor do Imposto</Form.Label>
              <TextInputNumberFormik formik={formik} type="number" field="vlImposto" />
            </Col>
            <Col sm="4">
              <Form.Label style={{ marginTop: 10 }}>Valor da Multa</Form.Label>
              <TextInputNumberFormik formik={formik} type="number" field="vlMulta" />
            </Col>
          </Row>
          <br />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          disabled={formik.isSubmitting}
          onClick={() => {
            formik.handleSubmit();
          }}
        >
          {row?.sqPAF ? "Alterar" : "Salvar"}
        </Button>{" "}
        <Button variant="secondary" onClick={formik.handleReset} disabled={formik.isSubmitting || isLoading}>
          Limpar
        </Button>{" "}
        <Button variant="light" onClick={() => closeHandler()}>
          {secondaryLabel || "Cancelar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EfisModalInserirAssociacao;
